/**
 * The Assay mark.
 *
 * Inline rather than an `<img>` so its four tones can be driven by the theme
 * tokens — the mark re-steps for a dark surface instead of shipping as two
 * files that drift apart. The canonical geometry lives in
 * `src/consulting_agent/brand_assets/mark.svg`, which is what the Word and
 * HTML deliverables use; `test_brand.py` asserts this and that file describe
 * the same mark.
 *
 * Each tone states a role: pale for an enquiry that yielded nothing, the
 * stroke for a connection the agent drew, ink for load-bearing evidence, and
 * one orange dot for the finding. Never add a second orange dot — with two,
 * neither is the finding.
 *
 * Below 18px the pale tier is dropped and the grid coarsens to 3×3: at that
 * size the lightest tone is the first thing to disappear, and a scaled-down
 * full mark turns to mud.
 *
 * **`animate` draws it rather than showing it**, in the order the mark
 * argues in: the dots are already there because evidence is found and not
 * made, the connections are drawn because they are the agent's work, and the
 * finding arrives last because it follows from both. Roughly 3.5 seconds end
 * to end, which is why it belongs on a surface somebody arrives at once — a
 * landing page, a sign-in — and not on the application bar, where it would
 * replay on every route change. The small mark never animates: the reduction
 * drops the tier the drawing is about.
 *
 * **`loop` keeps it going** — the mark draws itself, holds, and takes itself
 * apart again, which is the tagline under it as motion rather than words.
 * Opt-in on top of `animate`, and the rule for where it belongs is *somebody
 * is waiting*: a repeating animation is only noise where nobody is. The front
 * door, which somebody may sit on. The answer being written in a run's
 * discussion, which is the one place the drawing is also literally true — the
 * dots are already there because the run found them, the connections are the
 * agent's work, and the finding follows from both. A deck being built. An
 * upload sending a data room's bytes, where the person cannot leave the page
 * and has nothing else to read. And every other wait, because `Spinner` *is*
 * the looping mark (#136) — a spinning circle said "loading" in nobody's
 * voice, on the one screen where the reader had nothing else to look at. Each of them mounts it with the wait and unmounts it with
 * the wait, so it starts over rather than becoming a logo that will not
 * settle. It is emphatically not the application bar.
 *
 * The animation is opt-in rather than the default so that adding the mark to
 * a new surface does not make it move there by accident.
 *
 * The supplied asset ended with a "Connecting the dots" caption, which is not
 * here: every surface the mark appears on already carries a line directly
 * beneath it — `Assay`, or a page heading — and a second
 * small line in the same register competes with the product's own name rather
 * than adding to it. The `.mark-caption` class is in the stylesheet for a
 * surface that has room, such as a marketing page.
 */
import { BRAND } from "../lib/brand";

export function Mark({
  size = 24,
  className = "",
  animate = false,
  loop = false,
}: {
  size?: number;
  className?: string;
  animate?: boolean;
  loop?: boolean;
}) {
  if (size <= 18) return <MarkSmall size={size} className={className} />;
  // The loop variant carries its own name-duration-iteration set, so it
  // overrides the one-shot rather than fighting it.
  const link = animate
    ? `mark-link${loop ? " mark-link--loop" : ""}`
    : undefined;
  const finding = animate
    ? `mark-finding${loop ? " mark-finding--loop" : ""}`
    : undefined;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <g fill="var(--mark-checked)">
        <circle cx="48" cy="12" r="3" />
        <circle cx="12" cy="24" r="3" />
        <circle cx="24" cy="24" r="3" />
        <circle cx="48" cy="24" r="3" />
        <circle cx="48" cy="36" r="3" />
        <circle cx="48" cy="48" r="3" />
      </g>
      <g
        stroke="var(--mark-connection)"
        strokeWidth="2.2"
        fill="none"
        strokeLinecap="square"
      >
        {/*
          `pathLength` normalises all three to 100 units, so one
          `stroke-dasharray` draws the short link and the long one at the same
          rate. Without it each path would need its own length measured, and a
          wrong number leaves a line that never finishes drawing.
        */}
        <path className={link} pathLength={100} d="M12 12 H36 V36" />
        <path className={link && `${link} mark-link--2`} pathLength={100} d="M12 36 H36" />
        <path className={link && `${link} mark-link--3`} pathLength={100} d="M12 48 H36 V36" />
      </g>
      <g fill="var(--mark-evidence)">
        <circle cx="12" cy="12" r="3" />
        <circle cx="24" cy="12" r="3" />
        <circle cx="36" cy="12" r="3" />
        <circle cx="36" cy="24" r="3" />
        <circle cx="12" cy="36" r="3" />
        <circle cx="24" cy="36" r="3" />
        <circle cx="12" cy="48" r="3" />
        <circle cx="24" cy="48" r="3" />
        <circle cx="36" cy="48" r="3" />
      </g>
      <circle
        className={finding}
        cx="36"
        cy="36"
        r="6"
        fill="var(--signal)"
      />
    </svg>
  );
}

function MarkSmall({ size, className }: { size: number; className: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <g fill="var(--mark-checked)">
        <circle cx="51" cy="13" r="5" />
        <circle cx="13" cy="32" r="5" />
        <circle cx="51" cy="51" r="5" />
      </g>
      <g stroke="var(--mark-connection)" strokeWidth="4.5" fill="none">
        <path d="M13 13 H32 V32" />
        <path d="M13 51 H32 V32" />
      </g>
      <g fill="var(--mark-evidence)">
        <circle cx="13" cy="13" r="5" />
        <circle cx="32" cy="13" r="5" />
        <circle cx="51" cy="32" r="5" />
        <circle cx="13" cy="51" r="5" />
        <circle cx="32" cy="51" r="5" />
      </g>
      <circle cx="32" cy="32" r="9" fill="var(--signal)" />
    </svg>
  );
}

/**
 * The mark with the name beside it, as the application bar.
 *
 * The name alone. An owner line sat under it at 8px — the smallest text in
 * the product, on every page — and the product does not need to name its
 * maker to the people using it.
 */
export function Wordmark() {
  return (
    <span className="flex items-center gap-2.5">
      <Mark size={26} />
      <span className="text-base font-medium leading-none tracking-tight text-ink">
        {BRAND.name}
      </span>
    </span>
  );
}
